import { useState, useEffect } from 'react';
import { readerApi, familyApi } from '../../services/api';

const EmbeddedReaderManagement = ({ onComplete, onBack, isLoading, stepData }) => {
  const [readers, setReaders] = useState([]);
  const [newReader, setNewReader] = useState({ name: '', age: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    // Load existing readers if any
    loadReaders();
    
    // Debug: Log the stepData to see what family info is available
    console.log('🔍 EmbeddedReaderManagement stepData:', stepData);
  }, [stepData]);

  const loadReaders = async () => {
    try {
      console.log('🔍 Loading readers...');
      const response = await readerApi.getReaders();
      console.log('🔍 Readers response:', response.data);
      
      // API returns { readers } directly
      setReaders(response.data.readers || []);
    } catch (error) {
      console.error('Failed to load readers:', error);
    }
  };

  const handleAddReader = async () => {
    if (!newReader.name.trim()) {
      setError('Please enter a reader name');
      return;
    }

    setIsAdding(true);
    setError('');

    try {
      console.log('🔍 Creating reader with data:', { name: newReader.name.trim(), age: newReader.age });
      
      const response = await readerApi.createReader({
        name: newReader.name.trim(),
        age: newReader.age ? parseInt(newReader.age) : null
      });

      console.log('🔍 Reader creation response:', response.data);

      // API returns { message, reader } on success
      if (response.data.reader) {
        const createdReader = response.data.reader;
        
        // If we have a family from the previous onboarding step, add the reader to it
        if (stepData?.familyId) {
          try {
            console.log('🔍 Adding reader to family:', { familyId: stepData.familyId, readerId: createdReader.id });
            const familyResponse = await familyApi.addReaderToFamily(stepData.familyId, createdReader.id);
            console.log('🔍 Reader added to family response:', familyResponse.data);
          } catch (familyError) {
            console.error('Failed to add reader to family (non-critical):', familyError);
            // Don't fail the reader creation if family addition fails
            // The reader is still created successfully
          }
        } else {
          console.log('🔍 No family ID in stepData, skipping family association');
        }
        
        setReaders([...readers, createdReader]);
        setNewReader({ name: '', age: '' });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
      } else {
        setError('Unexpected response from server. Please try again.');
      }
    } catch (error) {
      console.error('Failed to add reader:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to add reader. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveReader = async (readerId) => {
    try {
      console.log('🔍 Removing reader:', readerId);
      const response = await readerApi.deleteReader(readerId);
      console.log('🔍 Reader deletion response:', response.data);
      
      // API returns { message } on success
      if (response.data.message) {
        setReaders(readers.filter(r => r.id !== readerId));
      } else {
        setError('Failed to remove reader. Please try again.');
      }
    } catch (error) {
      console.error('Failed to remove reader:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to remove reader. Please try again.');
    }
  };

  const handleComplete = () => {
    console.log('🔍 EmbeddedReaderManagement - completing with readers:', readers);
    onComplete({ 
      action: 'add_readers',
      readers: readers,
      readersAdded: readers.length
    });
  };

  return (
    <div className="embedded-reader-management">
      <div className="reader-header">
        <div className="reader-icon">📚</div>
        <h3>Add Family Readers</h3>
        <p>Add family members who will be reading together</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="success-message">
          Reader added successfully!
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Add New Reader Form */}
      <div className="add-reader-form">
        <h4>Add New Reader</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="readerName">Name *</label>
            <input
              type="text"
              id="readerName"
              value={newReader.name}
              onChange={(e) => setNewReader({ ...newReader, name: e.target.value })}
              placeholder="Enter reader's name"
              className="form-input"
              disabled={isAdding}
            />
          </div>
          <div className="form-group">
            <label htmlFor="readerAge">Age</label>
            <input
              type="number"
              id="readerAge"
              value={newReader.age}
              onChange={(e) => setNewReader({ ...newReader, age: e.target.value })}
              placeholder="Age"
              className="form-input"
              disabled={isAdding}
              min="1"
              max="120"
            />
          </div>
        </div>
        <button
          className="btn-primary"
          onClick={handleAddReader}
          disabled={isAdding || !newReader.name.trim()}
        >
          {isAdding ? 'Adding...' : 'Add Reader'}
        </button>
      </div>

      {/* Existing Readers List */}
      <div className="readers-list">
        <h4>Family Readers ({readers.length})</h4>
        {readers.length === 0 ? (
          <div className="no-readers">
            <p>No readers added yet. Add the first reader above!</p>
          </div>
        ) : (
          <div className="readers-grid">
            {readers.map(reader => (
              <div key={reader.id} className="reader-card">
                <div className="reader-info">
                  <div className="reader-name">{reader.name}</div>
                  <div className="reader-details">
                    {reader.age && <span>Age: {reader.age}</span>}
                    {reader.isPrimary && <span className="primary-badge">Primary</span>}
                  </div>
                </div>
                <button
                  className="remove-reader"
                  onClick={() => handleRemoveReader(reader.id)}
                  title="Remove reader"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="reader-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          className="btn-primary"
          onClick={handleComplete}
          disabled={isLoading}
        >
          {isLoading ? 'Continuing...' : 'Continue'}
        </button>
      </div>

      <style jsx>{`
        .embedded-reader-management {
          max-width: 600px;
          margin: 0 auto;
        }

        .reader-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .reader-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .reader-header h3 {
          margin: 0 0 8px 0;
          color: #1f2937;
          font-size: 20px;
        }

        .reader-header p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .success-message {
          background: #f0fdf4;
          color: #059669;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          border: 1px solid #bbf7d0;
          text-align: center;
        }

        .error-message {
          background: #fef2f2;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          border: 1px solid #fecaca;
        }

        .add-reader-form {
          background: #f9fafb;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .add-reader-form h4 {
          margin: 0 0 16px 0;
          color: #374151;
          font-size: 16px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          margin-bottom: 4px;
          font-weight: 500;
          color: #374151;
          font-size: 12px;
        }

        .form-input {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
        }

        .form-input:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
        }

        .readers-list {
          margin-bottom: 24px;
        }

        .readers-list h4 {
          margin: 0 0 16px 0;
          color: #374151;
          font-size: 16px;
        }

        .no-readers {
          text-align: center;
          padding: 40px 20px;
          background: #f9fafb;
          border-radius: 12px;
          border: 2px dashed #d1d5db;
        }

        .no-readers p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .readers-grid {
          display: grid;
          gap: 12px;
        }

        .reader-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .reader-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .reader-info {
          flex: 1;
        }

        .reader-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .reader-details {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: #6b7280;
          align-items: center;
        }

        .primary-badge {
          background: #dbeafe;
          color: #1d4ed8;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
        }

        .remove-reader {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s ease;
        }

        .remove-reader:hover {
          background: #fee2e2;
          transform: scale(1.1);
        }

        .reader-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }

          .reader-actions {
            flex-direction: column;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default EmbeddedReaderManagement;
